import React from 'react';
import { Box, makeStyles } from '@material-ui/core/index';
import PerfectScrollbar from 'react-perfect-scrollbar';

import Card from 'src/components/Card';
import Button from 'src/components/Button';
import useGetRequest from 'src/hooks/useGetRequest';
import { mapIterableByAttribute } from 'src/utils/groupIterableByAttribute';
import {
  Column, PerspectiveType, Widget
} from 'src/types';
import { RESOURCE_DETAILS_BY_RESOURCE_TYPE } from 'src/views/constants/tableRendererConstants';
import { LedgerColumn } from 'src/components';

import { useHistory, useLocation } from 'react-router';
import ColumnRenderer from './ColumnRenderer/ColumnRenderer';
import FakeLedgerRenderer from './FakeLedgerRenderer';
import LedgerRenderer from './LedgerRenderer';
import useModal from '../../../../../components/Modal/useModal';
import FormFromFormIdRenderer from '../FormFromFormIdRenderer';
import toCamelCase from '../../../../../utils/toCamelCase';

const useStyles = makeStyles(() => ({
  container: {
    overflow: 'hidden'
  },
}));

interface Props {
  widget: Widget,
  perspectiveType: PerspectiveType,
  workflowId: string,
  engagementId: string,
  openModal: (widget: Widget) => void,
  editMode?: boolean,
}

const TableRenderer = ({
  widget,
  perspectiveType,
  workflowId,
  engagementId,
  // openModal,
  editMode
}: Props) => {
  const {
    columns: selectedAttributesForWidget,
    resourceType,
    isCreateEnabled,
    buttonLabel,
    title,
  } = widget.data;
  const classes = useStyles();
  const {
    isOpen, openModal, closeModal
  } = useModal();
  const history = useHistory();
  const location = useLocation();

  // todo - strategy pattern this out
  const { result: table, isLoading: tableIsLoading } = useGetRequest({
    url: `/automatic/workflows/${workflowId}/table/`,
    initialResult: { fields: [] },
    shouldFetchOnInitialRender: Boolean(workflowId)
  });

  if (!resourceType || !selectedAttributesForWidget || !selectedAttributesForWidget.length || tableIsLoading) return <Card title="Unknown table" />;

  const resourceDetails = RESOURCE_DETAILS_BY_RESOURCE_TYPE[resourceType];

  const columns = resourceDetails.buildColumns({ fields: table.fields });
  const columnDetailsByColumnAttribute: Record<string, Column> = mapIterableByAttribute(columns, 'attribute');

  const ledgerColumns: LedgerColumn[] = selectedAttributesForWidget.map((attribute: string) => ({
    title: columnDetailsByColumnAttribute[attribute].label,
    cellContent: (item: Record<string, any>, ledgerProps: Record<string, any>) => (columnDetailsByColumnAttribute[attribute].getCell ? (
      // @ts-ignore
      columnDetailsByColumnAttribute[attribute].getCell(item, ledgerProps)
    ) : (
      <ColumnRenderer
        type={columnDetailsByColumnAttribute[attribute].type}
        item={item}
        attribute={attribute}
        isFake={editMode}
      />
    ))
  }));

  const actions = isCreateEnabled ? (
    <Button
      look="primary"
      onClick={() => {
        if (editMode) return;
        openModal();
      }}
    >
      {buttonLabel}
    </Button>
  ) : null;

  return (
    <>
      {
        isOpen && (
          <FormFromFormIdRenderer
            // @ts-ignore
            formId={widget.data.formId}
            workflowId={workflowId}
            closeModal={closeModal}
            onSuccess={() => {
              // reload page when form is submitted
              const current = location.pathname;
              history.push('/');
              history.push(current);
            }}
          />
        )
      }
      <PerfectScrollbar>
        <Box className={classes.container}>
          <Card title={title} p={0}>
            <Box display="flex" flexDirection="column" flex="1 1">
              {
                editMode ? (
                  <FakeLedgerRenderer
                    widget={widget}
                    actions={actions}
                    ledgerColumns={ledgerColumns}
                    columns={columns}
                  />
                ) : (
                  <LedgerRenderer
                    widget={widget}
                    perspectiveType={perspectiveType}
                    workflowId={workflowId}
                    engagementId={engagementId}
                    actions={actions}
                    ledgerColumns={ledgerColumns}
                    buildRoute={resourceDetails.buildRoute}
                  />
                )
              }
            </Box>
          </Card>
        </Box>
      </PerfectScrollbar>
    </>
  );
};

export default TableRenderer;
